import { Alert, List, Space, Spin, Tooltip, Typography } from 'antd';
import { RcFile } from 'antd/lib/upload';
import { PresetStatusColorType } from 'antd/lib/_util/colors';
import { FileOutlined } from '@ant-design/icons';
import { StyledDragger } from 'components/new-ui/base/dragger/styles';
import FileActions from 'components/new-ui/base/file-actions/FileActions';
import Tag from 'components/new-ui/base/tag/Tag';
import { H3 } from 'components/new-ui/base/typography/styles';
import { SecurityCheckStatus } from 'types/common';
import UploadFileButton from './UploadFileButton';
import { FileNameLink } from './styles';

export type AttachedFile = { id: string; fileName: string; securityChecksStatus?: SecurityCheckStatus };

/**
 * An uploaded file is scanned before it can be sent, and only a clean scan may go to Customs - so
 * the wait, and a scan that will never clear, both have to be visible on the row itself.
 */
const securityCheckTags: Record<SecurityCheckStatus, { label: string; status: PresetStatusColorType; hint: string }> = {
    VERIFIED: { label: 'Verified', status: 'success', hint: 'This file has passed its security check.' },
    PENDING: {
        label: 'Checking…',
        status: 'processing',
        hint: 'This file is still being checked for security. It can be sent once the check passes.',
    },
    MALICIOUS: {
        label: 'Blocked',
        status: 'error',
        hint: 'This file failed its security check and cannot be sent. Remove it and attach a different file.',
    },
    FAILED: {
        label: 'Check failed',
        status: 'error',
        hint: 'This file could not be checked for security and cannot be sent. Remove it and upload it again.',
    },
};

const getOpenBlockedReason = ({ securityChecksStatus }: AttachedFile): string | undefined => {
    if (!securityChecksStatus || securityChecksStatus === 'VERIFIED') return undefined;
    if (securityChecksStatus === 'PENDING')
        return 'This file is still being checked for security. Please try again shortly.';
    return 'This file failed its security check and cannot be opened.';
};

type Props<TFile extends AttachedFile> = {
    hint: string;
    requested?: string;
    acceptedTypes: string[];
    maxCount: number;
    documents: TFile[];
    isLoading: boolean;
    isUploading: boolean;
    canAddMore: boolean;
    disabled?: boolean;
    isRemovable?: (document: TFile) => boolean;
    onUpload: (file: RcFile) => void;
    onRemove: (document: TFile) => void;
    onDownload: (document: TFile) => void;
    onPreview: (document: TFile) => void;
};

const AttachmentsField = <TFile extends AttachedFile>({
    hint,
    requested,
    acceptedTypes,
    maxCount,
    documents,
    isLoading,
    isUploading,
    canAddMore,
    disabled = false,
    isRemovable = () => true,
    onUpload,
    onRemove,
    onDownload,
    onPreview,
}: Props<TFile>) => (
    <section>
        <H3>Attach Files</H3>
        <Typography.Text style={{ fontSize: 12 }}>
            {disabled
                ? documents.length
                    ? 'Sent to Customs with this answer.'
                    : 'No files were attached to this answer.'
                : `${hint} Customs accepts ${acceptedTypes.join(', ')} files only, up to ${maxCount} per request.`}
        </Typography.Text>

        {!disabled && requested && (
            <Alert
                style={{ marginTop: 8 }}
                type="info"
                showIcon
                message={`Customs asked for: ${requested}`}
                description="Nothing is sent back to say a document was missing, and this referral cannot be answered twice."
            />
        )}

        {!disabled && (
            <StyledDragger
                style={{ marginTop: 8 }}
                showUploadList={false}
                disabled={!canAddMore || isUploading}
                customRequest={({ file }) => onUpload(file as RcFile)}
            >
                {isUploading ? <Spin /> : <UploadFileButton />}
            </StyledDragger>
        )}

        {isLoading ? (
            <Spin style={{ marginTop: 8 }} />
        ) : (
            documents.length > 0 && (
                <List
                    style={{ marginTop: 8 }}
                    size="small"
                    dataSource={documents}
                    renderItem={(document) => {
                        const openBlockedReason = getOpenBlockedReason(document);
                        return (
                            <List.Item
                                actions={[
                                    <FileActions
                                        key="actions"
                                        preview={{
                                            onClick: () => onPreview(document),
                                            disabled: Boolean(openBlockedReason),
                                            tooltip: openBlockedReason ?? 'Preview',
                                        }}
                                        download={{
                                            onClick: () => onDownload(document),
                                            disabled: Boolean(openBlockedReason),
                                            tooltip: openBlockedReason ?? 'Download',
                                        }}
                                        delete={{
                                            onClick:
                                                disabled || !isRemovable(document)
                                                    ? undefined
                                                    : () => onRemove(document),
                                            tooltip: 'Remove from this answer',
                                        }}
                                    />,
                                ]}
                            >
                                <Space>
                                    <FileOutlined />
                                    {openBlockedReason ? (
                                        <Tooltip title={openBlockedReason}>
                                            <Typography.Text type="secondary">{document.fileName}</Typography.Text>
                                        </Tooltip>
                                    ) : (
                                        <FileNameLink type="button" onClick={() => onPreview(document)}>
                                            {document.fileName}
                                        </FileNameLink>
                                    )}
                                    {document.securityChecksStatus && (
                                        <Tooltip title={securityCheckTags[document.securityChecksStatus].hint}>
                                            {/* Tag renders no element of its own to hang the tooltip on. */}
                                            <span>
                                                <Tag {...securityCheckTags[document.securityChecksStatus]} />
                                            </span>
                                        </Tooltip>
                                    )}
                                </Space>
                            </List.Item>
                        );
                    }}
                />
            )
        )}

        {!disabled && documents.some((document) => document.securityChecksStatus === 'PENDING') && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Files are checked for security before they can be sent. This usually takes under a minute.
            </Typography.Text>
        )}
    </section>
);

export default AttachmentsField;
